import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CATEGORIES = [
    'Lecture & Theory',
    'Laboratories (Science & ICT)',
    'Engineering Workshops',
    'Vocational & Specialized',
    'Administrative & Staff'
];

const RoomTypeModal = ({ isOpen, onClose, onSave, roomType }) => {
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        category: CATEGORIES[0],
        requires_specialized_space: false,
        is_active: true,
        sort_order: 1000
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (roomType) {
            setFormData({
                code: roomType.code || '',
                name: roomType.name || '',
                category: roomType.category || CATEGORIES[0],
                requires_specialized_space: !!roomType.requires_specialized_space,
                is_active: roomType.is_active !== false,
                sort_order: roomType.sort_order || 1000
            });
        } else {
            setFormData({
                code: '',
                name: '',
                category: CATEGORIES[0],
                requires_specialized_space: false,
                is_active: true,
                sort_order: 1000
            });
        }
        setError('');
    }, [roomType, isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (!formData.code.trim()) return setError('Code is required.');
        if (!formData.name.trim()) return setError('Name is required.');
        if (!formData.category) return setError('Category is required.');

        setIsSubmitting(true);
        try {
            await onSave({
                ...formData,
                code: formData.code.trim().toUpperCase(),
                name: formData.name.trim(),
                sort_order: parseInt(formData.sort_order, 10) || 1000
            });
        } catch (err) {
            setError(err.message || 'An error occurred while saving the room type.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const isFallback = roomType && String(roomType.id).startsWith('fallback-');

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-background rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                <div className="flex justify-between items-center p-4 border-b border-border">
                    <h3 className="text-lg font-semibold text-foreground">
                        {roomType ? 'Edit Room Type' : 'Add Room Type'}
                    </h3>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
                        <Icon name="X" size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[calc(100vh-100px)] overflow-y-auto">
                    {error && (
                        <div className="p-3 text-sm text-red-600 bg-red-50 rounded border border-red-100">
                            {error}
                        </div>
                    )}
                    
                    {isFallback && (
                        <div className="p-3 text-sm text-amber-700 bg-amber-50 rounded border border-amber-100 mb-4">
                            You are editing a built-in room type. Saving this will create a database record that overrides the default settings.
                        </div>
                    )}

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">Code <span className="text-error">*</span></label>
                            <Input
                                value={formData.code}
                                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                placeholder="e.g. LECTURE_HALL"
                                disabled={isSubmitting || isFallback}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">Name <span className="text-error">*</span></label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="e.g. Lecture Hall"
                                disabled={isSubmitting}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">Category <span className="text-error">*</span></label>
                            <select
                                className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                disabled={isSubmitting}
                            >
                                {CATEGORIES.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">Sort Order</label>
                            <Input
                                type="number"
                                value={formData.sort_order}
                                onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                                placeholder="e.g. 1000"
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className="flex items-center mt-4">
                            <input
                                type="checkbox"
                                id="requires_specialized_space"
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                                checked={formData.requires_specialized_space}
                                onChange={(e) => setFormData({ ...formData, requires_specialized_space: e.target.checked })}
                                disabled={isSubmitting}
                            />
                            <label htmlFor="requires_specialized_space" className="ml-2 block text-sm text-foreground">
                                Requires Specialized Space (e.g. Labs, Workshops)
                            </label>
                        </div>

                        <div className="flex items-center mt-2">
                            <input
                                type="checkbox"
                                id="is_active"
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                disabled={isSubmitting}
                            />
                            <label htmlFor="is_active" className="ml-2 block text-sm text-foreground">
                                Active (available for selection)
                            </label>
                        </div>
                    </div>

                    <div className="pt-4 flex justify-end space-x-3 border-t border-border mt-6">
                        <Button variant="outline" type="button" onClick={onClose} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button variant="default" type="submit" disabled={isSubmitting}>
                            {isSubmitting ? (
                                <><Icon name="Loader" size={16} className="mr-2 animate-spin" /> Saving...</>
                            ) : (
                                'Save Room Type'
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RoomTypeModal;
